import { useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../redux/store';
import {
  addToPrescriptionStorageQueue,
  removeFromPrescriptionStorageQueue,
  incrementPrescriptionStorageRetry,
  setIsStoringPrescription,
  setPrescriptionStorageError,
  clearPrescriptionStorageError
} from '../redux/features/prescription/prescriptionSlice';
import { storeGuestPrescriptionToUser } from '../services/prescription.service';

export const usePrescriptionStorage = () => {
  const dispatch = useDispatch();
  
  const prescriptionStorageQueue = useSelector(
    (state: RootState) => state.prescriptionEvent.prescriptionStorageQueue
  );
  const isStoringPrescription = useSelector(
    (state: RootState) => state.prescriptionEvent.isStoringPrescription
  );
  const prescriptionStorageError = useSelector(
    (state: RootState) => state.prescriptionEvent.prescriptionStorageError
  );
  const authToken = useSelector(
    (state: RootState) => state.authEvent.authToken
  );

  // Function to queue a prescription for storage
  const queuePrescriptionStorage = useCallback((
    prescriptionId: string,
    pdId?: string
  ) => {
    if (!prescriptionId) {
      console.warn('queuePrescriptionStorage: Missing prescriptionId');
      return;
    }
    
    dispatch(addToPrescriptionStorageQueue({ prescriptionId, pdId }));
  }, [dispatch]);

  // Function to process the storage queue
  const processStorageQueue = useCallback(async () => {
    if (isStoringPrescription || prescriptionStorageQueue.length === 0 || !authToken) {
      return;
    }

    dispatch(setIsStoringPrescription(true));
    dispatch(clearPrescriptionStorageError());

    try {
      // Process items in the queue
      for (const item of prescriptionStorageQueue) {
        const success = await storeGuestPrescriptionToUser(
          item.prescriptionId,
          authToken,
          item.pdId,
          3 // max retries
        );

        if (success) {
          // Remove from queue on success
          dispatch(removeFromPrescriptionStorageQueue({ prescriptionId: item.prescriptionId }));
        } else {
          // Increment retry count on failure
          dispatch(incrementPrescriptionStorageRetry({ prescriptionId: item.prescriptionId }));
          
          // If max retries reached, remove from queue and log error
          if (item.retryCount >= 2) { // 3 total attempts (0, 1, 2)
            dispatch(removeFromPrescriptionStorageQueue({ prescriptionId: item.prescriptionId }));
            dispatch(setPrescriptionStorageError(
              `Failed to store prescription ${item.prescriptionId} after multiple attempts`
            ));
          }
        }
      }
    } catch (error: any) {
      dispatch(setPrescriptionStorageError(error.message || 'Failed to process prescription storage queue'));
    } finally {
      dispatch(setIsStoringPrescription(false));
    }
  }, [isStoringPrescription, prescriptionStorageQueue, authToken, dispatch]);

  // Auto-process queue when auth token becomes available
  useEffect(() => {
    if (authToken && prescriptionStorageQueue.length > 0 && !isStoringPrescription) {
      processStorageQueue();
    }
  }, [authToken, prescriptionStorageQueue.length, isStoringPrescription, processStorageQueue]);

  // Clean up old items from queue (older than 1 hour)
  useEffect(() => {
    const oneHourAgo = Date.now() - (60 * 60 * 1000);
    const oldItems = prescriptionStorageQueue.filter(item => item.timestamp < oneHourAgo);
    
    oldItems.forEach(item => {
      dispatch(removeFromPrescriptionStorageQueue({ prescriptionId: item.prescriptionId }));
    });
  }, [prescriptionStorageQueue, dispatch]);

  return {
    queuePrescriptionStorage,
    processStorageQueue,
    isStoringPrescription,
    prescriptionStorageError,
    prescriptionStorageQueue,
    clearError: () => dispatch(clearPrescriptionStorageError())
  };
}; 